import { parseISO } from "date-fns";
import { ArrowLeft, Ban, ExternalLink, RotateCcw } from "lucide-react";
import type { ReactNode } from "react";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { JsonTable } from "~/components/json-table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
} from "~/components/ui";
import { useDbpjkitWorkerJobRunCancellation } from "~/features/dbpjkitWorkerJobs/hooks";
import type { JobSummary, LatestRun } from "~/features/dbpjkitWorkerJobs/types";
import { detailSpreadsheetUrl, statusTone } from "~/features/dbpjkitWorkerJobs/utils/transformers";
import { cn } from "~/lib/utils";
import { DbpjkitWorkerJobRerunDialog } from "./DbpjkitWorkerJobRerunDialog";
import { DbpjkitWorkerJobRunsHistory } from "./DbpjkitWorkerJobRunsHistory";

interface DbpjkitWorkerJobDetailPresenterProps {
  job: JobSummary;
  latestRun?: LatestRun;
  onBack: () => void;
  /** "page" = full-page route (own width + back button); "peek" = inside the Sheet. */
  variant?: "page" | "peek";
}

export function DbpjkitWorkerJobDetailPresenter({
  job,
  latestRun,
  onBack,
  variant = "page",
}: DbpjkitWorkerJobDetailPresenterProps) {
  const { t } = useTranslation();
  const isPeek = variant === "peek";
  const spreadsheetUrl = detailSpreadsheetUrl(job.googleSpreadsheetId, job.googleSheetId);
  const { onCancel, canceling } = useDbpjkitWorkerJobRunCancellation();
  const [rerunDialogOpen, setRerunDialogOpen] = useState(false);
  // Holds the run awaiting abort confirmation. The dialog has to be a Radix one: this
  // presenter also renders inside the peek Sheet, and a modal Radix layer sets
  // `pointer-events: none` on <body>, so an overlay portalled outside that layer tree
  // would render but not accept clicks.
  const [abortRunId, setAbortRunId] = useState<string | null>(null);
  // A run can only be aborted while still queued or executing; terminal runs
  // (succeeded/failed/canceled) have nothing left to stop.
  const isCancelable = latestRun != null && (latestRun.statusId === "init" || latestRun.statusId === "in_progress");
  // Rerun is offered whenever nothing is currently running: latest run terminal,
  // or no persisted runs at all (dry-run-only jobs never persist a run). The
  // dialog re-validates against Cnut on open, so this is just a UI-level gate.
  const isRerunCandidate = !isCancelable;

  return (
    <div className={cn("space-y-5", isPeek ? "p-1" : "mx-auto max-w-[var(--content-max)] p-8")}>
      <div className="space-y-1">
        {!isPeek && (
          <Button type="button" variant="ghost" size="sm" className="px-0 text-fg-2" onClick={onBack}>
            <ArrowLeft />
            <span>{t("ui.back")}</span>
          </Button>
        )}
        <h1 className={cn("text-fg-1", isPeek ? "text-16-bold" : "text-dewey-title")}>{job.commandName}</h1>
      </div>

      <Card>
        <h2 className="text-14-bold capitalize text-fg-1">{t("ui.jobInformation")}</h2>
        <dl className="grid gap-3">
          <InfoRow label={t("model.dbpjkitWorkerJob.commandName")}>{job.commandName}</InfoRow>
          <InfoRow label={t("model.dbpjkitWorkerJob.googleSpreadsheetID")}>
            <span className="inline-flex items-center gap-1.5">
              <span className="tabular-nums">{job.googleSpreadsheetId}</span>
              {spreadsheetUrl && (
                <a
                  href={spreadsheetUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-fg-link hover:text-fg-1"
                  aria-label={t("model.dbpjkitWorkerJob.googleSpreadsheetID")}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </span>
          </InfoRow>
          <InfoRow label={t("model.dbpjkitWorkerJob.googleSheetID")}>
            <span className="tabular-nums">{job.googleSheetId}</span>
          </InfoRow>
          <InfoRow label={t("model.common.createdAt")}>
            {job.createdAt ? t("date.full", { date: parseISO(job.createdAt) }) : "—"}
          </InfoRow>
          <InfoRow label={t("model.common.updatedAt")}>
            {job.updatedAt ? t("date.full", { date: parseISO(job.updatedAt) }) : "—"}
          </InfoRow>
          {job.arguments != null && job.arguments !== "" && (
            <div className="flex flex-col gap-1.5">
              <dt className="text-12-bold capitalize text-fg-2">{t("model.dbpjkitWorkerJob.arguments")}</dt>
              <dd>
                <JsonTable target={job.arguments} />
              </dd>
            </div>
          )}
        </dl>
      </Card>

      <div className="space-y-1.5">
        <h2 className="text-12-bold capitalize text-fg-2">{t("ui.latestRun")}</h2>
        <div className="flex items-center gap-2">
          {latestRun ? (
            <>
              <Badge tone={statusTone(latestRun.statusId)}>{latestRun.statusId}</Badge>
              <span className="tabular-nums text-14-regular text-fg-2">
                {latestRun.queuedAt ? t("date.full", { date: parseISO(latestRun.queuedAt) }) : "—"}
              </span>
              {isCancelable && (
                <Button
                  type="button"
                  variant="danger-outline"
                  size="sm"
                  className="ml-auto"
                  disabled={canceling}
                  onClick={() => setAbortRunId(latestRun.id)}
                >
                  <Ban className="h-3.5 w-3.5" />
                  <span>{t("ui.abort")}</span>
                </Button>
              )}
            </>
          ) : (
            <span className="text-14-regular text-fg-3">—</span>
          )}
          {isRerunCandidate && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => setRerunDialogOpen(true)}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span>{t("ui.rerun")}</span>
            </Button>
          )}
        </div>
      </div>

      <DbpjkitWorkerJobRerunDialog jobId={job.id} open={rerunDialogOpen} onOpenChange={setRerunDialogOpen} />

      <AlertDialog
        open={abortRunId !== null}
        onOpenChange={(open) => {
          if (!open && !canceling) setAbortRunId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("feature.common.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("ui.abortX", { x: t("ui.thisJob") })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={canceling}>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={canceling}
              onClick={async (event) => {
                // Radix closes on click by default; keep the dialog up so the disabled
                // state stays visible until the mutation (and its refetches) settle.
                event.preventDefault();
                if (!abortRunId) return;
                if (await onCancel(abortRunId)) setAbortRunId(null);
              }}
            >
              {t("ui.abort")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Suspense fallback={<div className="text-[13px] text-fg-3">…</div>}>
        <DbpjkitWorkerJobRunsHistory jobId={job.id} jobName={job.commandName} />
      </Suspense>
    </div>
  );
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-12-bold capitalize text-fg-2">{label}</dt>
      <dd className="text-14-regular text-fg-1">{children}</dd>
    </div>
  );
}
